import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {Box, Hide, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment, useState} from 'react';

import {always, defaultTo, filter, length} from 'ramda';
import {isPositive} from 'ramda-adjunct';

import {useGetCurrentUserInfoQuery, useGetUserDashboardSettingsQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {myProfileRoutes, testIds} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {ClockRow} from './components/ClockRow';
import {LatestBusinessCases} from './components/LatestBusinessCases/LatestBusinessCases';
import {LatestInterests} from './components/LatestInterests/LatestInterests';
import {LatestVehicles} from './components/LatestVehicles/LatestVehicles';
import {OpportunitiesFunnel} from './components/OpportunitiesFunnel';
import {Support} from './components/Support';
import {TasksAndNotifications} from './components/TasksAndNotifications';
import {VehicleWarehouse} from './components/VehicleWarehouse';
import {DashboardLayoutConfig, DashboardSource} from './types/layoutBlocks';
import {splitBlocksToCols} from './utils/splitBlocksToCols';

const defaultToFalse = defaultTo(false);

export function Dashboard() {
  const [
    canReadInterest,
    canReadBusinessCase,
    canViewBusinessCasePurchase,
    canViewBusinessCaseSelling,
  ] = usePermissions({
    permissionKeys: [
      'interestRead',
      'businessCaseRead',
      'viewBusinessCasePurchase',
      'viewBusinessCaseSelling',
    ],
    scopes: {
      interestRead: {
        // EP for latest interests handles participation scope
        participation: 'SKIP',
        // EP for latest interests handles branch scope
        branchId: 'SKIP',
      },
      viewBusinessCasePurchase: {
        // EP for latest BCs handles participation scope
        participation: 'SKIP',
        // EP for latest BCs handles branch scope
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        // EP for latest BCs handles participation scope
        participation: 'SKIP',
        // EP for latest BCs handles branch scope
        branchId: 'SKIP',
      },
    },
  });

  const [dashboardSource, setDashboardSource] = useState<DashboardSource>(
    DashboardSource.activeBranch
  );
  const navigate = useNavigate();

  const {data: userInfo} = useGetCurrentUserInfoQuery();
  const {data: userDashboardSettings, isLoading: isUserDashboardSettingsLoading} =
    useGetUserDashboardSettingsQuery();

  const selectedBranchId = match(dashboardSource)
    .with(
      DashboardSource.activeBranch,
      // There is edge case, when user is new and has no branch set, you have to check defaultBranch
      () => userInfo?.settings?.branch || userInfo?.defaultBranch?.id
    )
    .otherwise(always(undefined));

  const branchName = userInfo?.branches?.find(
    (branch) => branch.id === userInfo?.settings?.branch
  )?.name;

  const isFunnelInterestsEnabled = defaultToFalse(userDashboardSettings?.salesFunnelInterests);
  const isFunnelBusinessCasesEnabled = defaultToFalse(
    userDashboardSettings?.salesFunnelBusinessCases
  );

  const isFunnelVisible = defaultToFalse(
    (isFunnelInterestsEnabled || isFunnelBusinessCasesEnabled) && canReadBusinessCase
  );

  const canViewLatestBusinessCase = canViewBusinessCasePurchase || canViewBusinessCaseSelling;

  const blocksDefinition: DashboardLayoutConfig = [
    {
      id: 'vehicle-warehouse',
      component: (
        <VehicleWarehouse
          isActiveBranch={dashboardSource === 'activeBranch'}
          branchId={selectedBranchId}
        />
      ),
      priority: 'left',
      isVisible: defaultToFalse(userDashboardSettings?.vehicleStock && canReadBusinessCase),
    },
    {
      id: 'latest-vehicles',
      component: <LatestVehicles branchId={selectedBranchId} />,
      priority: 'left',
      isVisible: defaultToFalse(userDashboardSettings?.latestVehicles),
    },
    {
      id: 'support',
      component: <Support />,
      priority: 'right',
      isVisible: true,
    },
    {
      id: 'notifications-and-tasks',
      component: <TasksAndNotifications />,
      priority: 'right',
      isVisible: defaultToFalse(
        userDashboardSettings?.notifications || userDashboardSettings?.myTasks
      ),
    },
    {
      id: 'latest-business-cases',
      component: <LatestBusinessCases branchId={selectedBranchId} />,
      priority: 'left',
      isVisible: defaultToFalse(
        userDashboardSettings?.latestBusinessCases &&
          canViewLatestBusinessCase &&
          canReadBusinessCase
      ),
    },
    {
      id: 'latest-interests',
      component: <LatestInterests branchId={selectedBranchId} />,
      priority: 'left',
      isVisible: defaultToFalse(
        userDashboardSettings?.latestInterests && canReadInterest && canReadBusinessCase
      ),
    },
  ];
  const visibleBlocks = filter((block) => block.isVisible, blocksDefinition);

  const blocksInColumns = splitBlocksToCols(visibleBlocks);

  return (
    <>
      <Helmet>
        <title>{i18n.t('page.homepage.labels.title')}</title>
      </Helmet>
      <Box padding={[2, 4]} data-testid={testIds.dashboard.home('wrapper')}>
        <VStack spacing={[3, 4]}>
          <ClockRow
            dashboardSource={dashboardSource}
            setDashboardSource={setDashboardSource}
            branchName={branchName}
          />

          <DataStatus
            isEmpty={!isFunnelVisible && !isPositive(length(visibleBlocks))}
            emptySubheadline={i18n.t('page.homepage.labels.noDataDashboard')}
            action={{
              title: i18n.t('general.labels.goToSettings'),
              variant: 'secondary',
              onClick: () => navigate(myProfileRoutes.dashboard),
            }}
            minHeight={80}
            isLoading={isUserDashboardSettingsLoading}
          >
            <Show when={isFunnelVisible}>
              <OpportunitiesFunnel
                branchId={selectedBranchId}
                isFunnelInterestsEnabled={isFunnelInterestsEnabled}
                isFunnelBusinessCasesEnabled={isFunnelBusinessCasesEnabled}
              />
            </Show>

            <Show onMobile>
              <VStack spacing={[3, 4]}>
                {visibleBlocks.map((block) => (
                  <Fragment key={block.id}>{block.component}</Fragment>
                ))}
              </VStack>
            </Show>

            <Hide onMobile>
              <HStack spacing={4}>
                <VStack spacing={4} basis="50%" maxWidth="50%">
                  {blocksInColumns.left.map((block) => (
                    <Fragment key={block.id}>{block.component}</Fragment>
                  ))}
                </VStack>
                <VStack spacing={4} basis="50%" maxWidth="50%">
                  {blocksInColumns.right.map((block) => (
                    <Fragment key={block.id}>{block.component}</Fragment>
                  ))}
                </VStack>
              </HStack>
            </Hide>
          </DataStatus>
        </VStack>
      </Box>
    </>
  );
}
